import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useBodyScrollLock, useDialogA11y } from './useDialog';

const EASE = [0.16, 1, 0.3, 1] as const;

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Largura máxima do card (default 480px). */
  maxWidth?: number;
  className?: string;
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Fechar"
      className="grid size-11 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
    >
      <X className="size-5" />
    </button>
  );
}

/** Cabeçalho compartilhado por Modal e BottomSheet (título opcional + X). */
export function DialogHeader({ title, onClose }: { title?: string; onClose: () => void }) {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-surface px-4 pb-1 pt-3 sm:px-5 sm:pt-4">
      <h2 className="text-lg font-semibold text-ink">{title ?? ''}</h2>
      <CloseButton onClose={onClose} />
    </div>
  );
}

/** Overlay + card centralizado (desktop). Fecha em Escape, clique no overlay e no X. */
export function Modal({ open, onClose, title, children, maxWidth = 480, className }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(open);
  useDialogA11y(open, onClose, panelRef);

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: EASE }}
          onClick={onClose}
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
        >
          <m.div
            key="panel"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2, ease: EASE }}
            style={{ maxWidth }}
            className={cn(
              'max-h-[90dvh] w-full overflow-y-auto rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-modal)] transition-[max-width] duration-200 focus:outline-none',
              className,
            )}
          >
            <DialogHeader title={title} onClose={onClose} />
            <div className="px-4 pb-4 sm:px-5 sm:pb-5">{children}</div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
